import { ReactNode } from 'react'
import { Border, EdgeInsets, Pad } from '../rendering'
import { Decorated } from './decorated'
import { Padding } from './padding'
import { Constrained } from './constrained'
import { Align, AlignmentValue } from './align'

type Props = {
  alignment?: AlignmentValue
  padding?: EdgeInsets | number
  border?: Border
  backgroundColor?: string
  width?: number
  height?: number
  minWidth?: number
  maxWidth?: number
  minHeight?: number
  maxHeight?: number
  children?: ReactNode
}

export function Box({
  alignment,
  padding,
  border,
  backgroundColor,
  width,
  height,
  minWidth,
  maxWidth,
  minHeight,
  maxHeight,
  children
}: Props) {
  let node = <>{children}</>

  if (alignment) {
    // When Box has explicit dimensions, use widthFactor/heightFactor
    // to prevent Align from expanding to fill parent
    const hasExplicitWidth = width !== undefined
    const hasExplicitHeight = height !== undefined

    node = (
      <Align
        alignment={alignment}
        widthFactor={hasExplicitWidth ? 1 : undefined}
        heightFactor={hasExplicitHeight ? 1 : undefined}
      >
        {node}
      </Align>
    )
  }

  if (padding) {
    node = <Padding padding={padding}>{node}</Padding>
  }

  const needsConstraints =
    width !== undefined ||
    height !== undefined ||
    minWidth !== undefined ||
    maxWidth !== undefined ||
    minHeight !== undefined ||
    maxHeight !== undefined

  if (needsConstraints) {
    node = (
      <Constrained
        minWidth={width !== undefined ? width : minWidth}
        maxWidth={width !== undefined ? width : maxWidth}
        minHeight={height !== undefined ? height : minHeight}
        maxHeight={height !== undefined ? height : maxHeight}
      >
        {node}
      </Constrained>
    )
  }

  if (border || backgroundColor) {
    node = (
      <Decorated border={border} backgroundColor={backgroundColor}>
        {node}
      </Decorated>
    )
  }

  return node
}
